import { Box, Stack, Typography } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import dayjs from "dayjs";

export const PERIOD_DURATION_SECONDS = 3600;

export const formatHoverTimeRange = (
  start: string,
  end: string,
  timeZone: string
): string => {
  const startFormatted = dayjs(start).tz(timeZone).format("YYYY-MM-DD HH:mm");
  const endFormatted = dayjs(end).tz(timeZone).format("HH:mm");
  return `${startFormatted} – ${endFormatted}`;
};

export const timelineChartLayout = (start: Date, end: Date, theme: Theme) => {
  const gridColor = alpha(theme.palette.divider, 0.4);
  return {
    height: 200,
    margin: { l: 50, r: 50, t: 10, b: 30 },
    xaxis: {
      type: "date" as const,
      range: [start, end],
      showgrid: true,
      gridcolor: gridColor,
    },
    yaxis: {
      visible: false,
      fixedrange: true,
    },
    bargap: 0.05,
    barmode: "overlay" as const,
    hovermode: "x unified" as const,
    dragmode: "pan" as const,
  };
};

export const stackedChartLayout = (start: Date, end: Date, theme: Theme) => {
  const gridColor = alpha(theme.palette.divider, 0.4);
  return {
    height: 200,
    margin: { l: 50, r: 50, t: 10, b: 30 },
    xaxis: {
      type: "date" as const,
      range: [start, end],
      showgrid: true,
      gridcolor: gridColor,
    },
    yaxis: {
      visible: true,
      fixedrange: true,
      range: [0, 1],
      tickvals: [0, 0.5, 1],
      ticktext: ["0%", "50%", "100%"],
      showgrid: false,
    },
    bargap: 0.05,
    barmode: "stack" as const,
    hovermode: "closest" as const,
    dragmode: "pan" as const,
  };
};

export const timelineChartConfig = {
  responsive: true,
  scrollZoom: false,
};

interface TimeWindow {
  window_start: string;
  window_end: string;
}

interface StackedBarStage<K extends string> {
  key: K;
  color: string;
  name?: string;
}

export interface StackedBarTrace {
  x: Date[];
  y: number[];
  type: "bar";
  marker: { color: string; line: { color: string; width: number } };
  width: number[];
  name: string;
  hovertext: string[];
  hoverinfo: "text";
  showlegend: false;
}

export const buildStackedBarTraces = <W extends TimeWindow, K extends string>({
  windows,
  stageConfig,
  computeBands,
  hoverTextFn,
  lineColor,
}: {
  windows: readonly W[];
  stageConfig: readonly StackedBarStage<K>[];
  computeBands: (window: W) => Record<K, number>;
  hoverTextFn: (window: W) => string;
  lineColor: string;
}): StackedBarTrace[] => {
  const bounds = windows.map((window) => ({
    start: new Date(window.window_start).getTime(),
    end: new Date(window.window_end).getTime(),
  }));
  const xValues = bounds.map(({ start, end }) => new Date((start + end) / 2));
  const widths = bounds.map(({ start, end }) => end - start);
  const hoverTexts = windows.map(hoverTextFn);
  const bands = windows.map(computeBands);

  return stageConfig.map((stage) => ({
    x: xValues,
    y: bands.map((band) => band[stage.key]),
    type: "bar" as const,
    marker: {
      color: stage.color,
      line: { color: lineColor, width: 0.1 },
    },
    width: widths,
    name: stage.name ?? stage.key,
    hovertext: hoverTexts,
    hoverinfo: "text" as const,
    showlegend: false,
  }));
};

export const TimelineLegend = ({
  color,
  label,
}: {
  color: string;
  label: string;
}) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: 0.5,
        backgroundColor: color,
      }}
    />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);
